/**
 * DAWMixer.jsx - Professional mixer panel for the DAW
 * 
 * Features:
 * - Channel strips with faders and meters
 * - Pan knobs
 * - Mute/Solo/Arm buttons
 * - VU meters with peak hold
 * - Master channel
 * - FX sends
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

// =============================================================================
// VU METER COMPONENT
// =============================================================================

export const VUMeter = ({ level = 0, peak = 0, isClipping = false, height = 120 }) => {
  const meterRef = useRef(null);
  const [displayPeak, setDisplayPeak] = useState(0);
  const peakHoldRef = useRef(null);
  
  // Peak hold with decay
  useEffect(() => {
    if (peak > displayPeak) {
      setDisplayPeak(peak);
      if (peakHoldRef.current) clearTimeout(peakHoldRef.current);
      peakHoldRef.current = setTimeout(() => {
        setDisplayPeak(0);
      }, 1500);
    }
  }, [peak, displayPeak]);
  
  // Draw meter
  useEffect(() => {
    const canvas = meterRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const h = canvas.height;
    
    ctx.clearRect(0, 0, width, h);
    
    // Background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, h);
    
    // Draw scale markers
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    const dbMarks = [0, -3, -6, -12, -18, -24, -36, -48];
    
    dbMarks.forEach(db => {
      const y = h * (1 - (db + 60) / 60);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    });
    
    // Calculate meter height
    const levelDb = level > 0 ? 20 * Math.log10(level) : -60;
    const normalizedLevel = Math.max(0, Math.min(1, (levelDb + 60) / 60));
    const meterHeight = normalizedLevel * h;
    
    // Draw gradient fill
    const gradient = ctx.createLinearGradient(0, h, 0, 0);
    gradient.addColorStop(0, '#22c55e');
    gradient.addColorStop(0.6, '#22c55e');
    gradient.addColorStop(0.75, '#eab308');
    gradient.addColorStop(0.9, '#f97316');
    gradient.addColorStop(1, '#ef4444');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, h - meterHeight, width, meterHeight);
    
    // Draw peak indicator
    if (displayPeak > 0) {
      const peakDb = 20 * Math.log10(displayPeak);
      const normalizedPeak = Math.max(0, Math.min(1, (peakDb + 60) / 60));
      const peakY = h * (1 - normalizedPeak);
      
      ctx.fillStyle = isClipping ? '#ef4444' : '#ffffff';
      ctx.fillRect(0, peakY, width, 2);
    }
    
    // Clip indicator
    if (isClipping) {
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(0, 0, width, 4);
    }
    
  }, [level, displayPeak, isClipping, height]);
  
  return (
    <canvas
      ref={meterRef}
      width={10}
      height={height}
      style={{ width: 10, height }}
      className="rounded-sm"
    />
  );
};

// =============================================================================
// PAN KNOB COMPONENT
// =============================================================================

export const PanKnob = ({ value = 0, onChange, size = 32 }) => {
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startValue = useRef(0);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startValue.current = value;
    e.preventDefault();
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const delta = (startY.current - e.clientY) / 100;
      const newValue = Math.max(-1, Math.min(1, startValue.current + delta));
      onChange?.(newValue);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onChange]);
  
  // Double-click to reset
  const handleDoubleClick = () => {
    onChange?.(0);
  };
  
  // Calculate rotation angle (-135 to 135 degrees)
  const angle = value * 135;
  
  return (
    <div
      ref={knobRef}
      className="relative cursor-pointer select-none"
      style={{ width: size, height: size }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      title={`Pan: ${value > 0 ? `R${Math.round(value * 100)}` : value < 0 ? `L${Math.round(Math.abs(value) * 100)}` : 'C'}`}
    >
      <svg viewBox="0 0 32 32" className="w-full h-full">
        {/* Background circle */}
        <circle cx="16" cy="16" r="14" fill="#374151" stroke="#4b5563" strokeWidth="1" />
        
        {/* Indicator marks */}
        <circle cx="16" cy="16" r="10" fill="none" stroke="#6b7280" strokeWidth="1" strokeDasharray="2 3" />
        
        {/* Center dot */}
        <circle cx="16" cy="16" r="3" fill="#9ca3af" />
        
        {/* Pointer */}
        <line
          x1="16"
          y1="16"
          x2="16"
          y2="5"
          stroke={isDragging ? '#a855f7' : '#e5e7eb'}
          strokeWidth="2"
          strokeLinecap="round"
          transform={`rotate(${angle}, 16, 16)`}
        />
      </svg>
      
      {/* Value display */}
      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[8px] text-gray-400">
        {value > 0 ? 'R' : value < 0 ? 'L' : 'C'}
      </div>
    </div>
  );
};

// =============================================================================
// FADER COMPONENT
// =============================================================================

export const Fader = ({ value = 1, onChange, height = 100 }) => {
  const faderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Convert linear 0-2 to fader position
  const positionFromValue = (v) => {
    // Use logarithmic scale for better control
    // 0 = -inf, 1 = 0dB (middle), 2 = +6dB
    if (v === 0) return 0;
    const db = 20 * Math.log10(v);
    const normalized = (db + 60) / 66; // -60dB to +6dB range
    return Math.max(0, Math.min(1, normalized));
  };
  
  // Convert fader position to value
  const valueFromPosition = (pos) => {
    const db = pos * 66 - 60;
    if (db <= -60) return 0;
    return Math.pow(10, db / 20);
  };
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateValue(e);
    e.preventDefault();
  };
  
  const updateValue = (e) => {
    const rect = faderRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const y = e.clientY - rect.top;
    const pos = 1 - y / rect.height;
    const newValue = valueFromPosition(Math.max(0, Math.min(1, pos)));
    onChange?.(newValue);
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => updateValue(e);
    const handleMouseUp = () => setIsDragging(false);
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  const position = positionFromValue(value);
  const db = value > 0 ? 20 * Math.log10(value) : -Infinity;
  
  return (
    <div 
      ref={faderRef}
      className="relative bg-gray-700 rounded cursor-pointer"
      style={{ width: 24, height }}
      onMouseDown={handleMouseDown}
    >
      {/* Track */}
      <div className="absolute inset-x-2 inset-y-1 bg-gray-800 rounded" />
      
      {/* Unity (0dB) marker */}
      <div 
        className="absolute left-0 right-0 h-px bg-gray-500"
        style={{ top: `${(1 - positionFromValue(1)) * 100}%` }}
      />
      
      {/* Fill */}
      <div 
        className="absolute left-2 right-2 bottom-1 bg-gradient-to-t from-purple-600 to-purple-400 rounded-b"
        style={{ height: `${position * (height - 8)}px` }}
      />
      
      {/* Fader handle */}
      <div
        className="absolute left-0 right-0 h-6 bg-gray-300 rounded shadow-md flex items-center justify-center"
        style={{ top: `${(1 - position) * (height - 24)}px` }}
      >
        <div className="w-4 h-px bg-gray-600" />
        <div className="absolute w-4 h-px bg-gray-600" style={{ marginTop: 4 }} />
        <div className="absolute w-4 h-px bg-gray-600" style={{ marginTop: -4 }} />
      </div>
      
      {/* dB display */}
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] text-gray-400 whitespace-nowrap">
        {db === -Infinity ? '-∞' : `${db.toFixed(0)}dB`}
      </div>
    </div>
  );
};

// =============================================================================
// CHANNEL STRIP COMPONENT
// =============================================================================

export const ChannelStrip = ({
  track,
  index,
  isSelected,
  onSelect,
  onMute,
  onSolo,
  onArm,
  onVolumeChange,
  onPanChange,
  level = 0,
  peak = 0
}) => {
  const trackColors = {
    purple: '#a855f7',
    blue: '#3b82f6',
    green: '#22c55e',
    red: '#ef4444',
    yellow: '#eab308',
    cyan: '#06b6d4',
    orange: '#f97316',
    pink: '#ec4899'
  };
  
  const color = trackColors[track.color] || trackColors.purple;
  
  return (
    <div
      className={`flex flex-col items-center p-2 bg-gray-800 border-r border-gray-700 ${
        isSelected ? 'bg-gray-750 ring-1 ring-purple-500' : ''
      }`}
      style={{ width: 70 }}
      onClick={() => onSelect?.(track.id)}
    >
      {/* Track number and color */}
      <div 
        className="w-full h-1 rounded mb-2"
        style={{ backgroundColor: color }}
      />
      
      {/* Pan knob */}
      <PanKnob
        value={track.pan || 0}
        onChange={(v) => onPanChange?.(track.id, v)}
        size={28}
      />
      
      <div className="h-2" />
      
      {/* Meter and fader */}
      <div className="flex gap-1">
        <VUMeter level={level} peak={peak} height={100} />
        <Fader
          value={track.volume}
          onChange={(v) => onVolumeChange?.(track.id, v)}
          height={100}
        />
      </div>
      
      <div className="h-2" />
      
      {/* Buttons */}
      <div className="flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); onMute?.(track.id); }}
          className={`w-5 h-5 rounded text-[9px] font-bold ${
            track.muted ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          M
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSolo?.(track.id); }}
          className={`w-5 h-5 rounded text-[9px] font-bold ${
            track.solo ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          S
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onArm?.(track.id); }}
          className={`w-5 h-5 rounded text-[9px] font-bold ${
            track.armed ? 'bg-red-600 text-white animate-pulse' : 'bg-gray-700 text-red-400 hover:bg-gray-600'
          }`}
        >
          ●
        </button>
      </div>
      
      {/* Track name */}
      <div className="mt-2 text-[10px] text-gray-300 truncate w-full text-center" title={track.name}>
        {track.name}
      </div>
      
      {/* Track number */}
      <div className="text-[8px] text-gray-500">
        {index + 1}
      </div>
    </div>
  );
};

// =============================================================================
// MASTER CHANNEL COMPONENT
// =============================================================================

export const MasterChannel = ({
  volume,
  onVolumeChange,
  levelL = 0,
  levelR = 0,
  peakL = 0,
  peakR = 0
}) => {
  return (
    <div className="flex flex-col items-center p-2 bg-gray-900 border-l-2 border-purple-600" style={{ width: 90 }}>
      <div className="text-[10px] text-purple-400 font-bold mb-2">MASTER</div>
      
      {/* Stereo meters and fader */}
      <div className="flex gap-1">
        <VUMeter level={levelL} peak={peakL} height={100} />
        <VUMeter level={levelR} peak={peakR} height={100} />
        <Fader
          value={volume}
          onChange={onVolumeChange}
          height={100}
        />
      </div>
      
      <div className="mt-2 text-[9px] text-gray-400">
        {volume > 0 ? `${(20 * Math.log10(volume)).toFixed(1)}dB` : '-∞'}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN MIXER COMPONENT
// =============================================================================

export const DAWMixer = ({
  tracks,
  masterVolume,
  selectedTrackId,
  onTrackSelect,
  onTrackMute,
  onTrackSolo,
  onTrackArm,
  onTrackVolumeChange,
  onTrackPanChange,
  onMasterVolumeChange,
  trackLevels = {},
  masterLevelL = 0,
  masterLevelR = 0
}) => {
  return (
    <div className="flex bg-gray-850 border-t border-gray-700 overflow-x-auto">
      {/* Channel strips */}
      {tracks.map((track, index) => (
        <ChannelStrip
          key={track.id}
          track={track}
          index={index}
          isSelected={selectedTrackId === track.id}
          onSelect={onTrackSelect}
          onMute={onTrackMute}
          onSolo={onTrackSolo}
          onArm={onTrackArm}
          onVolumeChange={onTrackVolumeChange}
          onPanChange={onTrackPanChange}
          level={trackLevels[track.id]?.level || 0}
          peak={trackLevels[track.id]?.peak || 0}
        />
      ))}
      
      {/* Spacer */}
      <div className="flex-1 min-w-[20px] bg-gray-900" />
      
      {/* Master channel */}
      <MasterChannel
        volume={masterVolume}
        onVolumeChange={onMasterVolumeChange}
        levelL={masterLevelL}
        levelR={masterLevelR}
        peakL={masterLevelL}
        peakR={masterLevelR}
      />
    </div>
  );
};

export default DAWMixer;
